import { useEffect, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { Send } from "lucide-react";
import { api } from "../lib/api";

const STARTERS = ["Why is my rule adherence low and what should I change first?", "How should I size my next trade with a 20-pip stop?", "How do I recover from a losing streak without revenge trading?", "What makes a strategy rule objective enough to be tested?"];

export default function CoachChat({ height = 520 }) {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sid, setSid] = useState(() => localStorage.getItem("xq_coach_sid") || "");
  const [usage, setUsage] = useState(null);
  const loc = useLocation();
  const endRef = useRef();

  useEffect(() => { api.get("/coach/usage").then((r) => setUsage(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (sid) api.get("/coach/history", { params: { session_id: sid } }).then((r) => setMsgs(r.data.map((m) => ({ role: m.role, content: m.content, sources: m.sources, kind: m.kind })))).catch(() => {}); }, []); // eslint-disable-line
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const ask = async (text) => {
    const q = (text || input).trim(); if (!q || busy) return;
    setInput(""); setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/coach/ask`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ message: q, page: loc.pathname, session_id: sid }) });
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      while (true) {
        const { value, done } = await reader.read(); if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) {
          const line = p.replace(/^data: /, ""); if (line === "[DONE]") continue;
          try {
            const ev = JSON.parse(line);
            if (ev.session_id) { setSid(ev.session_id); localStorage.setItem("xq_coach_sid", ev.session_id); }
            if (ev.delta) setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + ev.delta }; return c; });
            if (ev.meta) { setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], sources: ev.meta.sources, kind: ev.meta.kind }; return c; }); setUsage((u) => ({ ...(u || {}), remaining: ev.meta.remaining, limit: ev.meta.limit })); }
            if (ev.error) setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: "The coach is unavailable right now. Please try again." }; return c; });
          } catch {}
        }
      }
      if (res.status === 429) { const d = await res.json().catch(() => ({})); setMsgs((m) => [...m.slice(0, -1), { role: "assistant", content: d.detail || "You have used all your coach messages this month." }]); }
    } catch { setMsgs((m) => [...m.slice(0, -1), { role: "assistant", content: "Connection problem — please try again." }]); }
    setBusy(false);
  };
  const reset = () => { setMsgs([]); setSid(""); localStorage.removeItem("xq_coach_sid"); };

  return (
    <div className="coach-inline" data-testid="coach-chat">
      <div className="flex justify-between items-center mb-2">
        <div className="sub" style={{ margin: 0 }}>Every number comes from your own record. No buy or sell calls.</div>
        <button className="btn ghost sm" onClick={reset} data-testid="coach-new-btn">Start a new chat</button>
      </div>
      <div className="coach-msgs" style={{ height, maxHeight: "60vh" }} data-testid="coach-messages">
        {!msgs.length && <div><div className="sub">Try one of these:</div>{STARTERS.map((s) => <button key={s} className="opt" style={{ width: "100%", textAlign: "left" }} onClick={() => ask(s)} data-testid="coach-starter">{s}</button>)}</div>}
        {msgs.map((m, i) => <div key={i} className={`coach-msg ${m.role}`} data-testid={`coach-msg-${m.role}`}>{m.content || <span className="sub">thinking…</span>}{m.role === "assistant" && m.sources?.length > 0 && <div className="coach-sources" data-testid="coach-sources">{m.sources.map((s, j) => <Link key={j} to={s.link} className="chip" data-testid="coach-source-chip">{s.source} · {s.period}</Link>)}</div>}</div>)}
        <div ref={endRef} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex gap-2 mt-2">
        <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about risk, sizing, psychology, strategy…" data-testid="coach-input" />
        <button className="btn" disabled={busy} data-testid="coach-send-btn"><Send size={14} /> Ask</button>
      </form>
      <div className="coach-foot" data-testid="coach-disclaimer"><span>Educational analysis of your own data. Not investment advice.</span>{usage && <span className="mono" data-testid="coach-remaining">{usage.limit == null ? "Unlimited" : `${usage.remaining} of ${usage.limit} messages left this month`}</span>}</div>
    </div>
  );
}
